import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { DollarSign, Receipt, Wallet, ShieldAlert, Sparkles, Download } from "lucide-react";
import Money from "@/components/Money";
import { useCurrency } from "@/lib/currency";

const StatCard = ({ icon: Icon, label, value, accent, testId, sub }) => (
  <div className="p-6 rounded-2xl border border-border bg-white" data-testid={testId}>
    <div className={`h-9 w-9 rounded-xl flex items-center justify-center mb-4 ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="text-2xl font-display font-semibold">{value}</div>
    <div className="text-sm text-neutral-500 mt-1">{label}</div>
    {sub && <div className="text-xs text-neutral-400 mt-2">{sub}</div>}
  </div>
);

// Top-3 best-selling add-ons leaderboard. Empty state nudges vendors to enable extras.
const TopAddOnsCard = ({ items = [], totalBookings = 0 }) => {
  const hasData = items.length > 0;
  const maxRevenue = hasData ? Math.max(...items.map((x) => x.revenue || 0), 0.01) : 1;
  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid="payout-top-addons">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Best-selling extras</div>
          <h3 className="font-display text-2xl font-medium mt-1">Top add-ons</h3>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-neutral-500">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          {totalBookings} confirmed booking{totalBookings === 1 ? "" : "s"}
        </div>
      </div>
      {!hasData ? (
        <div className="rounded-xl bg-neutral-50/60 border border-dashed border-border p-6 text-center" data-testid="payout-top-addons-empty">
          <p className="text-sm text-neutral-600">No add-ons sold yet.</p>
          <p className="text-xs text-neutral-400 mt-1">Add baby seats, second drivers, or cross-border permits to your cars to unlock more revenue per trip.</p>
        </div>
      ) : (
        <ol className="space-y-3">
          {items.map((a, i) => {
            const pct = Math.round((a.attach_rate || 0) * 100);
            const widthPct = Math.max(6, Math.round(((a.revenue || 0) / maxRevenue) * 100));
            return (
              <li key={a.name} className="flex items-center gap-4" data-testid={`payout-top-addon-${i}`}>
                <div className="h-7 w-7 shrink-0 rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center text-xs font-semibold tabular-nums">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-neutral-900 truncate">{a.name}</div>
                    <div className="text-sm font-semibold tabular-nums shrink-0"><Money amount={a.revenue} /></div>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-neutral-500 flex-wrap">
                    <span data-testid={`payout-top-addon-attach-${i}`}>
                      <span className="font-semibold text-neutral-700">{pct}%</span> attach rate
                    </span>
                    <span className="text-neutral-300">·</span>
                    <span>{a.bookings} booking{a.bookings === 1 ? "" : "s"}</span>
                    {a.benchmark_label && a.benchmark_label !== "no-data" && (
                      <>
                        <span className="text-neutral-300">·</span>
                        <span data-testid={`payout-top-addon-benchmark-${i}`} className={
                          a.benchmark_label === "above"
                            ? "text-emerald-700"
                            : a.benchmark_label === "on-par"
                              ? "text-amber-700"
                              : "text-rose-700"
                        }>
                          {a.benchmark_label === "above" && (
                            <>Above market <span className="font-semibold">(top vendors {Math.round((a.marketplace_median_attach_rate || 0) * 100)}%)</span></>
                          )}
                          {a.benchmark_label === "on-par" && (
                            <>On par with market <span className="font-semibold">({Math.round((a.marketplace_median_attach_rate || 0) * 100)}%)</span></>
                          )}
                          {a.benchmark_label === "below" && (
                            <>Below market — top vendors hit <span className="font-semibold">{Math.round((a.marketplace_median_attach_rate || 0) * 100)}%</span></>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

/**
 * Iter 70d — Featured Slot monthly invoice downloader.
 *
 * Renders a compact month-picker + download button that streams
 * `/api/vendor/featured-invoice.pdf?year=Y&month=M` in a new tab.
 * Defaults to LAST completed month (the one accountants care about).
 * Uses a raw `fetch` (instead of the axios client) so the browser
 * treats the response as a downloadable file, and forwards the JWT.
 */
const FeaturedInvoiceDownload = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(lastMonth.getFullYear());
  const [month, setMonth] = useState(lastMonth.getMonth() + 1);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/vendor/featured-invoice.pdf?year=${year}&month=${month}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `featured-slot-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch {
      // no-op — the parent callout is optional info
    } finally {
      setBusy(false);
    }
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return (
    <div
      className="mt-3 flex items-center gap-2 flex-wrap"
      data-testid="payout-featured-invoice-picker"
    >
      <select
        value={month}
        onChange={(e) => setMonth(Number(e.target.value))}
        className="h-8 rounded-md border border-amber-300 bg-white/70 px-2 text-xs font-medium text-amber-900"
        data-testid="payout-featured-invoice-month"
      >
        {monthNames.map((n, i) => (
          <option key={n} value={i + 1}>{n}</option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => setYear(Number(e.target.value))}
        className="h-8 rounded-md border border-amber-300 bg-white/70 px-2 text-xs font-medium text-amber-900"
        data-testid="payout-featured-invoice-year"
      >
        {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      <button
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-1.5 h-8 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 disabled:opacity-50"
        data-testid="payout-featured-invoice-download"
      >
        <Download className="h-3 w-3" /> Download PDF
      </button>
    </div>
  );
};


export default function VendorPayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format, currency, rates } = useCurrency();
  const fxRate = rates?.[currency] || 1;

  useEffect(() => {
    let cancelled = false;
    api.get("/vendor/payouts")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { console.error("vendor payouts load failed:", e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-neutral-500 py-12 text-center">Loading payouts…</div>;
  if (!data) return <div className="text-neutral-500 py-12 text-center">Could not load payouts.</div>;

  const { summary, monthly, bookings } = data;
  const feePct = Math.round((summary.platform_fee_rate || 0) * 100);
  const isMonthly = summary.commission_mode === "monthly_per_car";
  const topAddOns = data.top_add_ons || [];

  return (
    <div className="space-y-8" data-testid="vendor-payouts-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Wallet}
          label="Owed to you"
          value={<Money amount={summary.owed} />}
          accent="bg-emerald-50 text-emerald-700"
          testId="payout-owed"
          sub={isMonthly ? `Net of €${summary.monthly_fee_this_month?.toFixed(2)} monthly platform fee` : "Net rental + any withheld deposits"}
        />
        <StatCard
          icon={DollarSign}
          label="Gross earnings"
          value={<Money amount={summary.gross} />}
          accent="bg-blue-50 text-blue-700"
          testId="payout-gross"
          sub={`${summary.bookings_count} confirmed bookings`}
        />
        <StatCard
          icon={Receipt}
          label={isMonthly ? `Monthly fee (€${summary.commission_value}/car)` : `Platform fee (${feePct}%)`}
          value={<Money amount={isMonthly ? (summary.monthly_fee_this_month || 0) : summary.platform_fee} />}
          accent="bg-neutral-100 text-neutral-700"
          testId="payout-fee"
          sub={isMonthly ? `${summary.monthly_fee_active_cars} active cars · billed monthly` : "On rental, route & insurance only"}
        />
        <StatCard
          icon={ShieldAlert}
          label="Withheld deposits"
          value={<Money amount={summary.withheld_deposits} />}
          accent="bg-rose-50 text-rose-700"
          testId="payout-withheld"
          sub="Damage / fines retained from customers"
        />
      </div>

      {/* Iter 70c — Featured slot fee callout. Only surfaces when the vendor
          has actually paid for a featured slot. Rendered in the amber theme
          that matches the homepage Featured badge so it reads as promo cost,
          not a penalty. */}
      {summary.featured_fee_total > 0 && (
        <div
          className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-yellow-50/40 p-5 flex items-start gap-3.5"
          data-testid="payout-featured-fee"
        >
          <div className="h-10 w-10 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-amber-900">Featured slot fee</div>
              <div
                className="tabular-nums font-bold text-amber-900"
                data-testid="payout-featured-fee-total"
              >
                <Money amount={summary.featured_fee_total} />
              </div>
            </div>
            <p className="text-sm text-amber-900/80 mt-1">
              Extra commission taken across your featured bookings in exchange for the
              homepage slot. Already deducted from the payouts below — this is just the
              running total for your records.
            </p>
            {/* Iter 70d — Monthly PDF invoice for accounting */}
            <FeaturedInvoiceDownload />
          </div>
        </div>
      )}

      {/* Iter 67az — refund-debt callout. Surfaces the cumulative amount the
          vendor owes the platform from cancellations that triggered a refund. */}
      {summary.refund_debt_total > 0 && (
        <div
          className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50/40 p-5 flex items-start gap-3.5"
          data-testid="payout-refund-debt"
        >
          <div className="h-10 w-10 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-amber-900">Refund debt owed to platform</div>
              <Money amount={summary.refund_debt_total} className="font-display text-lg font-semibold text-amber-900" />
            </div>
            <div className="text-sm text-amber-900/85 mt-1 leading-relaxed">
              {summary.refund_debt_bookings_count} cancelled booking{summary.refund_debt_bookings_count === 1 ? "" : "s"} were inside the free-cancellation window —
              the platform issued the renter refund and is recovering the cost from your next payout.
              Once Stripe Connect is enabled on your account, refunds will route directly through your balance.
            </div>
          </div>
        </div>
      )}

      <TopAddOnsCard items={topAddOns} totalBookings={summary.bookings_count} />

      <div className="rounded-2xl border border-border bg-white p-6" data-testid="payout-monthly-chart">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Last 12 months</div>
            <h3 className="font-display text-2xl font-medium mt-1">Payout trend</h3>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Net total</div>
            <div className="font-display text-2xl font-semibold"><Money amount={summary.net} /></div>
          </div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={monthly} margin={{ top: 8, right: 16, bottom: 0, left: -4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => format(v).replace(/[\d.,]+/, (n) => Number(n.replace(/,/g, "")).toFixed(0))} />
              <Tooltip formatter={(v) => format(v)} cursor={{ fill: "#f8fafc" }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="net" name="Net rental" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
              <Bar dataKey="payout" name="Total payout" stackId="b" fill="#10b981" radius={[6, 6, 0, 0]} hide={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Net = gross − platform fee. Total payout adds any withheld deposits (full amount, no platform fee).
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="payout-bookings-table">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-medium">Booking-by-booking</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Sorted by most recent. Withheld deposits appear in the right-most column.</p>
          </div>
        </div>
        {bookings.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 text-sm">No confirmed bookings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50/60 text-left">
                <tr>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500">Trip</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500">Customer</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500 text-right">Gross</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500 text-right">Fee</th>
                  {summary.featured_fee_total > 0 && (
                    <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-amber-700 text-right">
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Featured
                      </span>
                    </th>
                  )}
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500 text-right">Net</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500 text-right">+ Withheld</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-wider text-neutral-500 text-right">Payout</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t border-border" data-testid={`payout-row-${b.booking_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-neutral-900 truncate max-w-[200px]">{b.car_label}</div>
                      <div className="text-xs text-neutral-500">{(b.start_date || "").slice(0, 10)} → {(b.end_date || "").slice(0, 10)}</div>
                    </td>
                    <td className="px-5 py-3 text-neutral-600">{b.customer_name}</td>
                    <td className="px-5 py-3 text-right tabular-nums"><Money amount={b.gross} /></td>
                    <td className="px-5 py-3 text-right tabular-nums text-neutral-500">−<Money amount={b.platform_fee} /></td>
                    {summary.featured_fee_total > 0 && (
                      <td
                        className="px-5 py-3 text-right tabular-nums"
                        data-testid={`payout-row-featured-${b.booking_id}`}
                      >
                        {(b.featured_fee || 0) > 0 ? (
                          <span
                            className="text-amber-700"
                            title={
                              b.featured_fee_percent
                                ? `Featured slot at ${b.featured_fee_percent}%`
                                : "Featured slot fee"
                            }
                          >
                            −<Money amount={b.featured_fee} />
                          </span>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3 text-right tabular-nums font-medium"><Money amount={b.net} /></td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {b.withheld_deposit > 0 ? (
                        <Money amount={b.withheld_deposit} className="text-rose-700" />
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-700"><Money amount={b.payout} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
